import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import LocationSelectModal from '~/components/Content/LocationSelect/LocationSelectModal';

import { TripPlannerTripItemTypes } from '~/consts/tripPlanner';

import {
  type ActivityLocationImage,
  type AddActivityLocationImageRequest,
  type AddActivityLocationRequest,
  addActivityLocation,
  addActivityLocationImage,
  deleteActivityLocation,
  deleteActivityLocationImage,
  getActivityLocationImages,
} from '~/services/TripPlannerService';

import ImageLink from './ImageLink';

export const ACTIVITY_TYPES = {
  [TripPlannerTripItemTypes.ATTRACTION]: {
    value: TripPlannerTripItemTypes.ATTRACTION,
    label: 'Attraction',
  },
  [TripPlannerTripItemTypes.EXPERIENCE]: {
    value: TripPlannerTripItemTypes.EXPERIENCE,
    label: 'Experience',
  },
} as const;

export type ActivityType = keyof typeof ACTIVITY_TYPES;

export default function TripPlannerActivityLocation() {
  const [activities, setActivities] = useState<Array<ActivityLocationImage>>([]);
  const [filterPlaceId, setFilterPlaceId] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    activity: ActivityLocationImage | null;
  }>({
    open: false,
    activity: null,
  });

  // New location form state
  const [newLocation, setNewLocation] = useState<AddActivityLocationRequest>({
    name: '',
    placeId: '',
  });
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // New image form state
  const [newImage, setNewImage] = useState<AddActivityLocationImageRequest>({
    location: '',
    imageId: '',
    placeId: '',
  });
  const [isImageLocationModalOpen, setIsImageLocationModalOpen] = useState(false);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await getActivityLocationImages(undefined);
      if (response.status === 200) {
        setActivities(response.result);
      }
    } catch (error) {
      setActivities([]);
      console.error('Failed to fetch activities:', error);
    }
  }, []);

  const handleLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterPlaceId(e.target.value);
  };

  const handleLocationModalClose = () => {
    setIsLocationModalOpen(false);
  };

  const handleLocationSelected = (placeId: string, locationName: string) => {
    setNewLocation((prev) => ({
      ...prev,
      name: locationName,
      placeId: placeId,
    }));
    setIsLocationModalOpen(false);
  };

  const handleSubmitNewLocation = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await addActivityLocation(newLocation);
      if (response.status === 200) {
        setNewLocation({
          name: '',
          placeId: '',
        });
        fetchActivities();
        enqueueSnackbar('Location added successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to add location:', error);
      enqueueSnackbar('Failed to add location', { variant: 'error' });
    }
  };

  const handleNewLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewImage((prev) => ({ ...prev, location: e.target.value }));
  };

  const handlePlaceIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewImage((prev) => ({ ...prev, placeId: e.target.value }));
  };

  const handleImageIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewImage((prev) => ({ ...prev, imageId: e.target.value }));
  };

  const handleImageLocationSelected = (placeId: string, locationName: string) => {
    setNewImage((prev) => ({
      ...prev,
      location: locationName,
      placeId: placeId,
    }));
    setIsImageLocationModalOpen(false);
  };

  const handleSubmitNewImage = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await addActivityLocationImage(newImage);
      if (response.status === 200) {
        setNewImage((prev) => ({
          ...prev,
          imageId: '',
        }));
        fetchActivities();
        enqueueSnackbar('Image added successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to add image:', error);
      enqueueSnackbar('Failed to add image', { variant: 'error' });
    }
  };

  const handleDeleteImage = useCallback(
    async (activityId: string, imageId: string) => {
      try {
        const response = await deleteActivityLocationImage(activityId, imageId);
        if (response.status === 200) {
          fetchActivities();
          enqueueSnackbar('Image deleted successfully', { variant: 'success' });
        }
      } catch (error) {
        console.error('Failed to delete image:', error);
        enqueueSnackbar('Failed to delete image', { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchActivities],
  );

  const handleDeleteLocationClick = useCallback((activity: ActivityLocationImage) => {
    setDeleteConfirmation({ open: true, activity });
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmation({ open: false, activity: null });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmation.activity) {
      enqueueSnackbar('No location selected', { variant: 'error' });
      return;
    }

    try {
      const response = await deleteActivityLocation(deleteConfirmation.activity.id);
      if (response.status === 200) {
        fetchActivities();
        enqueueSnackbar('Location deleted successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to delete location:', error);
      enqueueSnackbar('Failed to delete location', { variant: 'error' });
    } finally {
      handleCloseDeleteConfirmation();
    }
  }, [deleteConfirmation.activity, enqueueSnackbar, fetchActivities, handleCloseDeleteConfirmation]);

  const filteredActivities = useMemo(() => {
    if (!filterPlaceId) {
      return activities;
    }
    return activities.filter((activity) => activity.placeId.includes(filterPlaceId));
  }, [activities, filterPlaceId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Location
      </Typography>

      {/* Add New Location Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add New Location
        </Typography>
        <Box component="form" onSubmit={handleSubmitNewLocation}>
          <Stack spacing={2}>
            <TextField
              label="Location Name"
              required
              fullWidth
              value={newLocation.name}
              disabled
              placeholder="Select a location using the button below"
            />
            <Button variant="outlined" fullWidth onClick={() => setIsLocationModalOpen(true)} sx={{ height: '56px' }}>
              {newLocation.name ? `Change Location (${newLocation.name})` : 'Select Location'}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" color="primary" disabled={!newLocation.placeId}>
                Add Location
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Add New Image Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add New Image
        </Typography>
        <Box component="form" onSubmit={handleSubmitNewImage}>
          <Stack spacing={2}>
            <TextField
              label="Location"
              required
              fullWidth
              value={newImage.location}
              onChange={handleNewLocationChange}
              placeholder="Enter location name"
            />
            <TextField
              label="Place ID"
              required
              fullWidth
              value={newImage.placeId}
              onChange={handlePlaceIdChange}
              placeholder="Enter place ID"
            />
            <TextField
              label="Image ID"
              required
              fullWidth
              value={newImage.imageId}
              onChange={handleImageIdChange}
              placeholder="Enter image ID"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" color="primary">
                Add Image
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          label="Filter by Place ID"
          value={filterPlaceId}
          onChange={handleLocationChange}
          placeholder="Filter by place ID"
        />
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Place ID</TableCell>
              <TableCell>Images</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredActivities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No locations found
                </TableCell>
              </TableRow>
            ) : (
              filteredActivities.map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell>{activity.name}</TableCell>
                  <TableCell>{activity.placeId}</TableCell>
                  <TableCell>
                    {activity.imageIds.map((imageId) => (
                      <ImageLink
                        key={imageId}
                        imageId={imageId}
                        activityId={activity.id}
                        onDelete={(activityId, imageId) => handleDeleteImage(activityId, imageId)}
                      />
                    ))}
                  </TableCell>
                  <TableCell align="center">
                    <Button color="error" onClick={() => handleDeleteLocationClick(activity)} size="small">
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onClose={handleCloseDeleteConfirmation}>
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>Are you sure you want to delete this location? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmation}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Selection Modals */}
      <LocationSelectModal
        open={isLocationModalOpen}
        onClose={handleLocationModalClose}
        onLocationSelected={handleLocationSelected}
      />
      <LocationSelectModal
        open={isImageLocationModalOpen}
        onClose={() => setIsImageLocationModalOpen(false)}
        onLocationSelected={handleImageLocationSelected}
      />
    </Container>
  );
}
